import React, { forwardRef } from 'react';
import { MapPin, Building2, Bed, Bath, Maximize, Phone, MessageCircle, Star } from 'lucide-react';
import './Brochure.css';

const formatPrice = (price) => {
  if (price == null || price === '') return null;
  const n = Number(price);
  if (!Number.isFinite(n) || n === 0) return null;
  if (n >= 10000000) return '₹' + (n / 10000000).toFixed(2) + ' Cr';
  if (n >= 100000) return '₹' + (n / 100000).toFixed(2) + ' L';
  return '₹' + n.toLocaleString('en-IN');
};

const formatDate = (d) => {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Field = ({ label, value }) => {
  if (value == null || value === '' || value === '-') return null;
  return (
    <div className="brochure__field">
      <span className="brochure__field-label">{label}</span>
      <span className="brochure__field-value">{value}</span>
    </div>
  );
};

/**
 * Premium 4-page brochure for Project or Property.
 * Renders offscreen; captured to PDF via html2canvas + jsPDF.
 *
 * Props:
 *   kind: 'project' | 'property'
 *   entity: full project/property data including media[]
 *   resolveImage: (url) => absolute url for capturing
 *   agent: { name, phone, email } — optional contact CTA info
 */
const Brochure = forwardRef(({ kind, entity, resolveImage = (u) => u, agent = {} }, ref) => {
  if (!entity) return null;

  const isProject = kind === 'project';
  const mediaList = Array.isArray(entity.media) ? entity.media : [];
  const imageMedia = mediaList.filter(m => {
    const url = m.file_url || m.url || '';
    const name = (m.file_name || '').toLowerCase();
    return /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(url) || /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(name) || (m.media_type || '').toLowerCase() === 'image';
  });
  const coverMedia = imageMedia.find(m => m.is_cover) || imageMedia[0];
  const coverUrl = coverMedia ? resolveImage(coverMedia.file_url || coverMedia.url) : null;
  const gallery = imageMedia.filter(m => m !== coverMedia).slice(0, 6);

  const title = isProject ? entity.name : (entity.title || 'Property');
  const subtitle = isProject
    ? (entity.developer || '')
    : (entity.project_name || '');
  const location = isProject
    ? [entity.city, entity.state].filter(Boolean).join(', ')
    : entity.location;
  const statusLabel = isProject ? entity.construction_status : entity.status;
  const typeLabel = isProject ? entity.project_type : entity.property_type;
  const price = !isProject ? formatPrice(entity.price) : null;

  const amenities = entity.amenities
    ? entity.amenities.split(',').map(a => a.trim()).filter(Boolean)
    : [];

  return (
    <div ref={ref} className="brochure">
      {/* ─── PAGE 1: COVER ─────────────────────────────── */}
      <div className="brochure__page brochure__page--cover">
        <div className="brochure__cover-image">
          {coverUrl ? (
            <img src={coverUrl} alt={title} crossOrigin="anonymous" />
          ) : (
            <div className="brochure__cover-placeholder">
              <Building2 size={80} />
            </div>
          )}
          <div className="brochure__cover-overlay" />
        </div>

        <div className="brochure__cover-top">
          <div className="brochure__brand">
            <div className="brochure__brand-mark"><img src="/leadboatlogo.png" alt="LeadBoat logo" /></div>
            <div>
              <div className="brochure__brand-name">LeadBoat</div>
              <div className="brochure__brand-sub">Premium Real Estate</div>
            </div>
          </div>
          <div className="brochure__cover-badge">
            {isProject ? 'PROJECT BROCHURE' : 'PROPERTY BROCHURE'}
          </div>
        </div>

        <div className="brochure__cover-content">
          {typeLabel && (
            <span className="brochure__cover-type">{typeLabel}</span>
          )}
          <h1 className="brochure__cover-title">{title}</h1>
          {subtitle && (
            <div className="brochure__cover-subtitle">{subtitle}</div>
          )}
          {location && (
            <div className="brochure__cover-location">
              <MapPin size={16} /> {location}
            </div>
          )}

          <div className="brochure__cover-meta">
            {statusLabel && (
              <span className="brochure__cover-pill">{statusLabel}</span>
            )}
            {price && (
              <span className="brochure__cover-price">{price}</span>
            )}
          </div>
        </div>

        <div className="brochure__cover-footer">
          <span>Exclusive Listing</span>
          <span>{new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</span>
        </div>
      </div>

      {/* ─── PAGE 2: HIGHLIGHTS / DETAILS ──────────────── */}
      <div className="brochure__page">
        <div className="brochure__page-header">
          <div className="brochure__page-num">02</div>
          <div className="brochure__page-title">
            <h2>{isProject ? 'Project Highlights' : 'Property Details'}</h2>
            <span>Key information at a glance</span>
          </div>
        </div>

        {price && (
          <div className="brochure__price-card">
            <span className="brochure__price-label">Price</span>
            <span className="brochure__price-value">{price}</span>
            {entity.price_per_sqft && (
              <span className="brochure__price-sub">
                {formatPrice(entity.price_per_sqft)} / sqft
              </span>
            )}
          </div>
        )}

        {!isProject && (
          <div className="brochure__quick-stats">
            {entity.bedrooms != null && (
              <div className="brochure__stat">
                <Bed size={18} />
                <strong>{entity.bedrooms}</strong>
                <span>Beds</span>
              </div>
            )}
            {entity.bathrooms != null && (
              <div className="brochure__stat">
                <Bath size={18} />
                <strong>{entity.bathrooms}</strong>
                <span>Baths</span>
              </div>
            )}
            {entity.area && (
              <div className="brochure__stat">
                <Maximize size={18} />
                <strong>{entity.area}</strong>
                <span>{entity.area_unit || 'sqft'}</span>
              </div>
            )}
            {entity.configuration && (
              <div className="brochure__stat">
                <Building2 size={18} />
                <strong>{entity.configuration}</strong>
                <span>Config</span>
              </div>
            )}
          </div>
        )}

        <div className="brochure__section">
          <h3 className="brochure__section-title">
            {isProject ? 'Project Information' : 'Specifications'}
          </h3>
          <div className="brochure__grid">
            {isProject ? (
              <>
                <Field label="Developer" value={entity.developer} />
                <Field label="RERA Number" value={entity.rera_number} />
                <Field label="Project Type" value={entity.project_type} />
                <Field label="Construction Status" value={entity.construction_status} />
                <Field label="Possession Date" value={formatDate(entity.possession_date)} />
                <Field label="Total Towers" value={entity.total_towers} />
                <Field label="Total Units" value={entity.total_units} />
                <Field label="City" value={entity.city} />
                <Field label="State" value={entity.state} />
                <Field label="Pincode" value={entity.pincode} />
              </>
            ) : (
              <>
                <Field label="Property Type" value={entity.property_type} />
                <Field label="Configuration" value={entity.configuration} />
                <Field label="Bedrooms" value={entity.bedrooms} />
                <Field label="Bathrooms" value={entity.bathrooms} />
                <Field label="Carpet Area" value={entity.carpet_area ? `${entity.carpet_area} ${entity.area_unit || 'sqft'}` : null} />
                <Field label="Built-up Area" value={entity.builtup_area ? `${entity.builtup_area} ${entity.area_unit || 'sqft'}` : null} />
                <Field label="Floor" value={entity.floor ? `${entity.floor}${entity.total_floors ? ' / ' + entity.total_floors : ''}` : null} />
                <Field label="Furnishing" value={entity.furnishing} />
                <Field label="Facing" value={entity.facing} />
                <Field label="Parking" value={entity.parking} />
                <Field label="Balconies" value={entity.balconies} />
                <Field label="Project" value={entity.project_name} />
              </>
            )}
          </div>
        </div>

        {entity.description && (
          <div className="brochure__section">
            <h3 className="brochure__section-title">About</h3>
            <p className="brochure__description">{entity.description}</p>
          </div>
        )}

        {(entity.address || location) && (
          <div className="brochure__section">
            <h3 className="brochure__section-title">
              <MapPin size={14} /> Location
            </h3>
            <p className="brochure__address">
              {entity.address || location}
            </p>
          </div>
        )}

        <div className="brochure__page-footer">
          <span>LeadBoat &middot; Premium Real Estate</span>
          <span>Page 02</span>
        </div>
      </div>

      {/* ─── PAGE 3: GALLERY ───────────────────────────── */}
      {gallery.length > 0 && (
        <div className="brochure__page">
          <div className="brochure__page-header">
            <div className="brochure__page-num">03</div>
            <div className="brochure__page-title">
              <h2>Gallery</h2>
              <span>A glimpse of the {isProject ? 'project' : 'property'}</span>
            </div>
          </div>

          <div className={`brochure__gallery brochure__gallery--${Math.min(gallery.length, 6)}`}>
            {gallery.map((m, i) => (
              <div key={m.media_id || i} className="brochure__gallery-item">
                <img
                  src={resolveImage(m.file_url || m.url)}
                  alt={`Gallery ${i + 1}`}
                  crossOrigin="anonymous"
                />
              </div>
            ))}
          </div>

          <div className="brochure__page-footer">
            <span>LeadBoat &middot; Premium Real Estate</span>
            <span>Page 03</span>
          </div>
        </div>
      )}

      {/* ─── PAGE 4: AMENITIES + CONTACT ───────────────── */}
      <div className="brochure__page">
        <div className="brochure__page-header">
          <div className="brochure__page-num">{gallery.length > 0 ? '04' : '03'}</div>
          <div className="brochure__page-title">
            <h2>Amenities &amp; Contact</h2>
            <span>Everything you need, all in one place</span>
          </div>
        </div>

        {amenities.length > 0 && (
          <div className="brochure__section">
            <h3 className="brochure__section-title">
              <Star size={14} /> Amenities &amp; Features
            </h3>
            <div className="brochure__amenities">
              {amenities.map((a, i) => (
                <span key={i} className="brochure__amenity">{a}</span>
              ))}
            </div>
          </div>
        )}

        {!isProject && (entity.owner_name || entity.owner_contact) && (
          <div className="brochure__section">
            <h3 className="brochure__section-title">Owner Details</h3>
            <div className="brochure__grid">
              <Field label="Owner Type" value={entity.owner_type} />
              <Field label="Owner Name" value={entity.owner_name} />
              <Field label="Contact" value={entity.owner_contact} />
            </div>
          </div>
        )}

        <div className="brochure__cta">
          <div className="brochure__cta-left">
            <h3>Interested in this {isProject ? 'project' : 'property'}?</h3>
            <p>Get in touch with our team for site visits, pricing details, and exclusive offers.</p>
            {agent.name && (
              <div className="brochure__agent">
                <div className="brochure__agent-avatar">
                  {agent.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="brochure__agent-name">{agent.name}</div>
                  <div className="brochure__agent-role">Sales Representative</div>
                </div>
              </div>
            )}
          </div>
          <div className="brochure__cta-right">
            {(agent.phone || true) && (
              <div className="brochure__contact-item">
                <Phone size={16} />
                <span>{agent.phone || '+91 99999 99999'}</span>
              </div>
            )}
            {agent.email && (
              <div className="brochure__contact-item">
                <MessageCircle size={16} />
                <span>{agent.email}</span>
              </div>
            )}
            <div className="brochure__contact-item">
              <MessageCircle size={16} />
              <span>WhatsApp Available</span>
            </div>
          </div>
        </div>

        <div className="brochure__disclaimer">
          This brochure is for informational purposes only. All specifications, prices, and details
          are subject to change without notice. Images may be artistic representations.
          Please verify all details before making any commitments.
        </div>

        <div className="brochure__page-footer">
          <span>LeadBoat &middot; Premium Real Estate</span>
          <span>Page {gallery.length > 0 ? '04' : '03'}</span>
        </div>
      </div>
    </div>
  );
});

Brochure.displayName = 'Brochure';

export default Brochure;
